import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Button,
    Alert,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Grid,
    Paper
} from '@mui/material';
import {
    Search as SearchIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    TableChart as TableChartIcon,
    CalendarToday as CalendarIcon,
    InsertDriveFile as FileIcon,
    Payment as PaymentIcon,
    PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { SelectableDataGrid } from '../DataGrid';
import { LoadingState, ErrorBoundary } from '../Common';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';

interface PreviewDataRow {
    id: string;
    [key: string]: any;
    'Valor'?: number;
    'Link Docuware'?: string;
}

interface AutorizarCPPreviewProps {
    fileName?: string;
    medioPago?: string;
    todayDate?: string;
    previewData?: PreviewDataRow[];
    columns?: string[];
    sourceJobId?: string;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

const parseNumericValue = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const cleanValue = value.replace(/[^\d.,]/g, '');
        return parseFloat(cleanValue.replace(/,/g, '')) || 0;
    }
    return 0;
};

const AutorizarCPPreview: React.FC<AutorizarCPPreviewProps> = ({
    fileName = '',
    medioPago = '',
    todayDate = '',
    previewData = [],
    columns = [],
    sourceJobId = ''
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<PreviewDataRow[]>([]);
    const [selectedRows, setSelectedRows] = useState<PreviewDataRow[]>([]);
    const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
    const [fechaCE, setFechaCE] = useState(todayDate || new Date().toISOString().split('T')[0]);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    // Initialize component data with enhanced debugging
    useEffect(() => {
        const initializeData = () => {
            try {
                console.log('🔍 INITIALIZING DATA...');
                console.log('📊 Raw previewData:', previewData);
                console.log('📊 previewData type:', typeof previewData);
                console.log('📊 previewData length:', previewData ? previewData.length : 'N/A');
                
                if (previewData && previewData.length > 0) {
                    const processedData = previewData.map((row, index) => {
                        const processedRow = {
                            ...row,
                            id: row.id || `row_${index}`
                        };
                        
                        // Debug first few rows
                        if (index < 3) {
                            console.log(`📋 ROW ${index}:`, processedRow);
                        }
                        
                        return processedRow;
                    });
                    
                    console.log('✅ DATA INITIALIZED:', processedData.length, 'rows');
                    console.log('🆔 First 3 IDs:', processedData.slice(0, 3).map(r => r.id));
                    
                    setData(processedData);
                    setLoading(false);
                } else {
                    console.warn('⚠️ No preview data available');
                    setError('No hay datos de previsualización disponibles');
                    setLoading(false);
                }
            } catch (err) {
                console.error('❌ Error initializing preview data:', err);
                setError('Error al cargar los datos de previsualización');
                setLoading(false);
            }
        };

        initializeData();
    }, [previewData]);


    // Create DataGrid columns
    const gridColumns: GridColDef[] = useMemo(() => {
        if (!columns || columns.length === 0) return [];

        return columns.map(col => {
            const baseColumn: GridColDef = {
                field: col,
                headerName: col,
                minWidth: 120,
                flex: 1,
            };

            if (col === 'Valor') {
                return {
                    ...baseColumn,
                    type: 'number',
                    width: 140,
                    valueFormatter: (params) => formatCurrency(parseNumericValue(params.value))
                };
            }

            if (col === 'Link Docuware') {
                return {
                    ...baseColumn,
                    width: 120,
                    sortable: false,
                    filterable: false,
                    renderCell: (params) => {
                        if (params.value && typeof params.value === 'string' && params.value.includes('href')) {
                            const hrefMatch = params.value.match(/href="([^"]+)"/);
                            if (hrefMatch) {
                                return (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        href={hrefMatch[1]}
                                        target="_blank"
                                    >
                                        Ver PDF
                                    </Button>
                                );
                            }
                        }
                        return params.value || '';
                    }
                };
            }

            return baseColumn;
        });
    }, [columns]);

    const handleSelectionAction = (selectedRows: PreviewDataRow[]) => {
        if (selectedRows.length === 0) {
            setSnackbar({
                open: true,
                message: 'Por favor seleccione al menos una fila para procesar.',
                severity: 'error'
            });
            return;
        }

        if (!fechaCE) {
            setSnackbar({
                open: true,
                message: 'Por favor seleccione una fecha para el Comprobante de Egreso.',
                severity: 'error'
            });
            return;
        }

        setConfirmDialog(true);
    };

    const handleSelectionChange = (selectedRowsData: PreviewDataRow[], selectedIds: GridRowSelectionModel) => {
        console.log('🎉 PARENT: Got', selectedRowsData.length, 'selected rows');
        console.log('🆔 Selected IDs:', selectedIds);
        console.log('📋 Selected rows data:', selectedRowsData);
        
        // Defensive programming - ensure we have valid data
        if (!Array.isArray(selectedRowsData)) {
            console.warn('⚠️ selectedRowsData is not an array:', selectedRowsData);
            setSelectedRows([]);
            setSelectedRowIds([]);
            return;
        }
        
        if (!Array.isArray(selectedIds)) {
            console.warn('⚠️ selectedIds is not an array:', selectedIds);
            setSelectedRows([]);
            setSelectedRowIds([]);
            return;
        }
        
        setSelectedRows(selectedRowsData);
        setSelectedRowIds(selectedIds);
    };

    const handleConfirmProcess = () => {
        setConfirmDialog(false);

        try {
            // Use the actual selected rows
            localStorage.setItem('selectedRowsData', JSON.stringify(selectedRows));
            localStorage.setItem('fechaCE', fechaCE);
            localStorage.setItem('mediopago', medioPago);
            localStorage.setItem('filename', fileName);

            // Redirect to confirmation page
            window.location.href = '/autorizar-cp-generar-ce-confirmar';
        } catch (err) {
            console.error('Error processing data:', err);
            setSnackbar({
                open: true,
                message: `Error: ${err instanceof Error ? err.message : 'Error desconocido'}`,
                severity: 'error'
            });
        }
    };

    if (loading) {
        return <LoadingState type="spinner" message="Cargando datos de previsualización..." />;
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <ErrorBoundary>
            <Box sx={{ p: 2 }}>
                <Card elevation={2}>
                    <CardHeader
                        title={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SearchIcon />
                                <Typography variant="h5">
                                    Previsualización de datos
                                </Typography>
                            </Box>
                        }
                        sx={{ bgcolor: 'primary.main', color: 'white' }}
                    />
                    <CardContent sx={{ p: 2.5 }}>
                        {/* Compact Alerts Section */}
                        <Box sx={{ mb: 3 }}>
                            {sourceJobId && (
                                <Alert severity="success" sx={{ mb: 1.5 }}>
                                    <strong>Datos importados desde programación de pagos aprobada.</strong> ID: {sourceJobId}
                                </Alert>
                            )}
                            <Alert severity="info" sx={{ mb: 1.5 }}>
                                Verifique que los datos sean correctos antes de proceder. Use filtros para buscar datos específicos.
                                <strong> Las filas filtradas marcadas seguirán siendo procesadas.</strong>
                            </Alert>
                        </Box>

                        {/* Compact Information Grid */}
                        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                            <Grid container spacing={3} alignItems="center">
                                {/* File Info */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <FileIcon color="primary" />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Archivo
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium" color="primary.main">
                                                {fileName}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* Payment Method */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <PaymentIcon color="primary" />
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Medio de pago
                                            </Typography>
                                            <Typography variant="body1" fontWeight="medium" color="primary.main">
                                                {medioPago}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                {/* Date Input */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <CalendarIcon color="primary" />
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                Fecha del CE
                                            </Typography>
                                            <TextField
                                                type="date"
                                                value={fechaCE}
                                                onChange={(e) => setFechaCE(e.target.value)}
                                                required
                                                size="small"
                                                fullWidth
                                                sx={{ 
                                                    '& .MuiInputBase-root': { 
                                                        fontWeight: 'medium',
                                                        color: 'primary.main'
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Data Table using SelectableDataGrid */}
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <TableChartIcon />
                            Datos a procesar:
                        </Typography>

                        <SelectableDataGrid
                            rows={data}
                            columns={gridColumns}
                            loading={loading}
                            showSelectionSummary={true}
                            selectionActions={[
                                {
                                    label: 'Verificar valores',
                                    color: 'success',
                                    icon: <PlayArrowIcon />,
                                    onClick: handleSelectionAction
                                }
                            ]}
                            checkboxSelection
                            onSelectionChange={handleSelectionChange}
                        />

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => window.location.href = '/autorizar-cp-generar-ce'}
                                size="medium"
                            >
                                Volver
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Confirmation Dialog */}
                <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
                    <DialogTitle>
                        <CheckCircleIcon sx={{ mr: 1 }} />
                        Confirmar procesamiento
                    </DialogTitle>
                    <DialogContent>
                        <Typography gutterBottom>
                            ¿Está seguro de que desea procesar los datos seleccionados?
                        </Typography>
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography><strong>Fecha CE:</strong> {fechaCE}</Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleConfirmProcess} variant="contained" color="success">
                            Continuar a confirmación
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ErrorBoundary>
    );
};

export default AutorizarCPPreview;